import { useEffect, useRef, useState } from "react";

import { OlgaEnvironmentsAPI } from "../providers/OlgaEnvironmentsAPI";
import { StandaloneCouponsAPI } from "../providers/StandaloneCouponsAPI";
import { Empty } from "../shared/components/Empty";
import { Loading } from "../shared/components/Loading";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements";
import { Icon } from "../shared/components/core/Icon";
import { BenefitList } from "../shared/containers/beneficios/BenefitList";
import { BenefitQrCode } from "../shared/containers/beneficios/BenefitQrCode";
import { useIsInViewport } from "../shared/hooks/useIsInViewport";
import { BenefitFormDrawer } from "shared/containers/beneficios/BenefitFormDrawer";
import styles from "../styles/beneficios/BenefitList.module.scss";

export default function BenefitFolder() {
  const [folder, setFolder] = useState({});
  const [benefits, setBenefits] = useState([]);
  const [hasNext, setHasNext] = useState(true);
  const [page, setPage] = useState(0);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [benefitToEdit, setBenefitToEdit] = useState({});
  const [loading, setLoading] = useState(true);

  const pageIndexMapRef = useRef({});

  const [pageLoading, setPageLoading] = useState(true);

  const loadingRef = useRef(null);
  const isVisible = useIsInViewport(loadingRef);

  const folderId = window.location.href.split("/")[5];

  useEffect(() => {
    if (folderId) {
      loadFolder();
    }
  }, [folderId]);

  useEffect(() => {
    if (hasNext && folderId && isVisible) {
      loadBenefits();
    }
  }, [isVisible, page, folderId]);

  const refreshItem = async (id) => {
    const itemPage = pageIndexMapRef.current[id];
    if (itemPage == null) return;

    const response = await StandaloneCouponsAPI.getStandaloneCoupons(
      "ACTIVATED",
      null,
      itemPage
    );

    const { items } = response.getData({
      items: [],
      next: { page: -1, limit: 12 },
    });

    const updated = items.find((it) => it.id === id);

    if (!updated) return;

    setBenefits((prev) =>
      prev.map((b) => {
        return b.id === id ? updated : b;
      })
    );
  };

  const loadBenefits = async (refresh) => {
    setLoading(true);

    const pageToLoad = refresh ? 0 : page;

    const response = await StandaloneCouponsAPI.getStandaloneCoupons(
      "ACTIVATED",
      folderId,
      pageToLoad
    );
    const benefitsPage = response.getData({
      items: [],
      next: { page: -1, limit: 12 },
    });

    benefitsPage.items.forEach((item) => {
      pageIndexMapRef.current[item.id] = pageToLoad;
    });

    let newBenefitsPage;

    if (refresh) {
      newBenefitsPage = [...benefitsPage.items];
    } else {
      newBenefitsPage = [...benefits, ...benefitsPage.items];
    }

    setBenefits(newBenefitsPage);

    const hasNextPage = benefitsPage.next.page !== -1;
    setHasNext(hasNextPage);

    if (hasNextPage) {
      setPage(benefitsPage.next.page);
    }

    setLoading(false);
  };

  const loadFolder = async () => {
    setPageLoading(true);

    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();
    const folders = environmentConfigurations.getData({})?.coupon_folders || [];
    const folder = folders.find((folder) => folder.id === folderId);
    setFolder(folder || {});

    setPageLoading(false);
  };

  return (
    <>
      {pageLoading ? (
        <div className="d-flex flex-column w-100">
          <Loading fullContent />
        </div>
      ) : (
        <>
          <Title
            title={folder?.name || ""}
            backTo={{ url: "/beneficios", name: "Benefícios" }}
          />

          <BenefitList
            benefits={benefits}
            onEditBenefit={(c) => {
              setBenefitToEdit(c);
              setFormVisible(true);
            }}
            onLoadBenefits={() => loadBenefits("refresh")}
          />

          <BenefitQrCode
            benefit={selectedBenefit}
            visible={selectedBenefit}
            onClose={() => setSelectedBenefit(null)}
          />

          <If cond={benefits.length === 0 && !hasNext && !loading}>
            <Empty>
              <Icon name="fa-ticket-simple" size="4x" />
              <p>Não há benefícios nesta pasta.</p>
            </Empty>
          </If>
        </>
      )}

      <If cond={hasNext}>
        <div className={styles.loading}>
          <Loading />
        </div>
        <div ref={loadingRef}></div>
      </If>

      <BenefitFormDrawer
        visible={formVisible}
        benefitToEdit={benefitToEdit}
        onSave={(benefitId) => {
          if (benefitId) refreshItem(benefitId);
          else loadBenefits("refresh");
        }}
        onClose={() => {
          setFormVisible(false);
          setBenefitToEdit(null);
        }}
      />
    </>
  );
}
